import React, { useId } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import RequiredLabel from './RequiredLabel';
import Dropdown from './Dropdown';
import SearchableSelect from './SearchableSelect';

// Same box as the Dropdown trigger (border colour, text size, padding, focus
// ring). Inputs were text-base with a lighter border and a top margin, so an
// input sat 4px lower and 4px taller than a Dropdown in the same row.
export const CONTROL_CLASSES = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const FormField = ({
  name,
  label,
  type = 'text',
  required = false,
  placeholder,
  options = [],
  validation = {},
  helpText,
  className = '',
  searchable = false,
  'data-testid': dataTestId,
  ...props
}) => {
  const { register, formState: { errors }, control } = useFormContext();
  const error = errors[name];
  const testIdProps = dataTestId ? { 'data-testid': dataTestId } : {};
  // Ties the <label> to a native input or textarea, so the field has an accessible name
  // (and a click on the label focuses it). The custom Dropdown is a button named by its
  // selected option, which a label[for] would replace, so it is left as it was.
  const generatedId = useId();
  const controlId = props.id || generatedId;
  const labelsControl = type !== 'select';

  const renderInput = () => {
    if (type === 'select') {
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => {
            if (searchable) {
              return (
                <SearchableSelect
                  value={field.value || ''}
                  onChange={field.onChange}
                  options={options}
                  placeholder={placeholder || `Select ${label.toLowerCase()}`}
                  className={className}
                  disabled={props.disabled}
                  {...testIdProps}
                />
              );
            }
            return (
              <Dropdown
                value={field.value || ''}
                onChange={field.onChange}
                className={className}
                {...testIdProps}
              >
                <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Dropdown>
            );
          }}
        />
      );
    }

    if (type === 'textarea') {
      return (
        <textarea
          id={controlId}
          {...register(name, validation)}
          placeholder={placeholder}
          // block: an inline-block textarea sits on the text baseline and
          // leaves a descender gap under it that no other control has.
          className={twMerge('block', CONTROL_CLASSES, className)}
          {...testIdProps}
          {...props}
        />
      );
    }

    if (type === 'checkbox') {
      return (
        <div className="inline-flex items-center mt-2">
          <input
            type="checkbox"
            {...register(name, validation)}
            className="form-checkbox h-4 w-4 text-primary-600"
            {...testIdProps}
            {...props}
          />
          <span className="ml-2 text-sm text-gray-700">{label}</span>
        </div>
      );
    }

    // Hide spinner arrows for number inputs
    const numberInputClasses = type === 'number' 
      ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
      : '';
    // Chrome pads the date editor's field wrapper 1px top and bottom, making it
    // 2px taller than a text input or Dropdown in the same row.
    const dateInputClasses = type === 'date' ? '[&::-webkit-datetime-edit-fields-wrapper]:py-0' : '';
    
    return (
      <input
        type={type}
        id={controlId}
        {...register(name, validation)}
        placeholder={placeholder}
        className={twMerge(CONTROL_CLASSES, numberInputClasses, dateInputClasses, className)}
        onWheel={type === 'number' ? (e) => e.target.blur() : undefined}
        {...testIdProps}
        {...props}
      />
    );
  };

  // For checkboxes, the label is handled differently
  if (type === 'checkbox') {
    return (
      <div>
        {renderInput()}
        {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
      </div>
    );
  }

  // No bottom margin: every form stacks fields with space-y or a grid gap. In a
  // stack the margin collapsed away; in a grid it did not, doubling the gap
  // under every multi-column row.
  return (
    <div>
      <label htmlFor={labelsControl ? controlId : undefined} className="block text-sm font-medium text-gray-700 mb-2">
        {required ? <RequiredLabel required={required}>{label}</RequiredLabel> : label}
        {helpText && <span className='text-xs text-gray-400 ml-1'>({helpText})</span>}
      </label>
      {renderInput()}
      {error && <p className="text-red-500 text-xs mt-2">{error.message}</p>}
    </div>
  );
};

export default FormField; 